/**
 * TaskList — Table view of tasks with inline status toggle
 */

import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar, User } from "lucide-react";
import type { AMTask, TaskStatus } from "@/hooks/useAMTasks";

const priorityConfig: Record<string, { label: string; className: string }> = {
  urgent: { label: "Urgent", className: "bg-red-100 text-red-700 border-red-200" },
  high: { label: "High", className: "bg-orange-100 text-orange-700 border-orange-200" },
  medium: { label: "Medium", className: "bg-blue-100 text-blue-700 border-blue-200" },
  low: { label: "Low", className: "bg-slate-100 text-slate-600 border-slate-200" },
};

const statusConfig: Record<TaskStatus, { label: string; className: string }> = {
  todo: { label: "To Do", className: "bg-slate-100 text-slate-700" },
  in_progress: { label: "In Progress", className: "bg-blue-100 text-blue-700" },
  done: { label: "Done", className: "bg-green-100 text-green-700" },
};

const sourceLabels: Record<string, string> = {
  manual: "Manual",
  auto_health: "Health",
  auto_sla: "SLA",
  auto_contract: "Contract",
  auto_engagement: "Engagement",
};

interface TaskListProps {
  tasks: AMTask[];
  agentNames: Map<string, string>;
  onTaskClick: (task: AMTask) => void;
  onToggleDone: (taskId: string, currentStatus: TaskStatus) => void;
}

export default function TaskList({ tasks, agentNames, onTaskClick, onToggleDone }: TaskListProps) {
  // Sort: undone first (urgent > high > medium > low), then by due date, done last
  const sorted = [...tasks].sort((a, b) => {
    if (a.status === "done" && b.status !== "done") return 1;
    if (a.status !== "done" && b.status === "done") return -1;
    const prioOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
    const prioDiff = prioOrder[a.priority] - prioOrder[b.priority];
    if (prioDiff !== 0) return prioDiff;
    if (a.dueDate && b.dueDate) return a.dueDate.localeCompare(b.dueDate);
    if (a.dueDate) return -1;
    if (b.dueDate) return 1;
    return 0;
  });

  if (sorted.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No tasks yet. Create one to get started.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-muted-foreground">
            <th className="pb-3 w-8"></th>
            <th className="pb-3 font-medium">Task</th>
            <th className="pb-3 font-medium">Client</th>
            <th className="pb-3 font-medium">Priority</th>
            <th className="pb-3 font-medium">Status</th>
            <th className="pb-3 font-medium">Due</th>
            <th className="pb-3 font-medium">Source</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((task) => {
            const prio = priorityConfig[task.priority];
            const st = statusConfig[task.status];
            const isOverdue = task.dueDate && task.status !== "done" && new Date(task.dueDate) < new Date();
            const isDone = task.status === "done";

            return (
              <tr
                key={task.id}
                className={`border-b last:border-0 hover:bg-muted/30 transition-colors ${isDone ? "opacity-60" : ""}`}
              >
                <td className="py-2.5">
                  <Checkbox
                    checked={isDone}
                    onCheckedChange={() => onToggleDone(task.id, task.status)}
                  />
                </td>
                <td
                  className="py-2.5 cursor-pointer"
                  onClick={() => onTaskClick(task)}
                >
                  <span className={`font-medium ${isDone ? "line-through text-muted-foreground" : "text-foreground"}`}>
                    {task.title}
                  </span>
                </td>
                <td className="py-2.5 text-muted-foreground">
                  {task.agentId ? (
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {agentNames.get(task.agentId) || "—"}
                    </span>
                  ) : "—"}
                </td>
                <td className="py-2.5">
                  <Badge variant="outline" className={`text-[10px] ${prio.className}`}>
                    {prio.label}
                  </Badge>
                </td>
                <td className="py-2.5">
                  <Badge variant="secondary" className={`text-[10px] ${st.className}`}>
                    {st.label}
                  </Badge>
                </td>
                <td className={`py-2.5 ${isOverdue ? "text-red-600 font-medium" : "text-muted-foreground"}`}>
                  {task.dueDate ? (
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {new Date(task.dueDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </span>
                  ) : "—"}
                </td>
                <td className="py-2.5 text-xs text-muted-foreground">
                  {sourceLabels[task.source] || task.source}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
